import { supabase } from "@/lib/supabase";
import { getStoredPollinationsKeys } from "@/lib/apiKeys";
import { normalizeImageApiPayload } from "@/lib/imageApiContract";
import { buildFlashcardImagePrompt } from "./flashcardImagePrompt";

export interface AiImageApiAttempt {
  provider: "pollinations";
  endpoint: string;
  model: string;
  authStrategy: "header" | "query" | "public";
  status?: number;
  errorCode?: string;
  error?: string;
  attempt: number;
}

export interface RequestAiImageOptions {
  prompt: string;
  term?: string;
  translationHint?: string;
  sourceLanguage?: string;
  targetLanguage?: string;
  mode?: "initial" | "regenerate";
  excludeImageUrls?: string[];
  excludeProviderAssetIds?: string[];
  variationSeed?: number;
  userId: string;
  pollinationsKeys?: string[];
}

export class AiImageClientError extends Error {
  errorCode?: string;
  status?: number;
  attempts?: AiImageApiAttempt[];

  constructor(message: string, options?: { errorCode?: string; status?: number; attempts?: AiImageApiAttempt[] }) {
    super(message);
    this.name = "AiImageClientError";
    this.errorCode = options?.errorCode;
    this.status = options?.status;
    this.attempts = options?.attempts;
  }
}

export function isRetryableAiImageError(error: unknown): boolean {
  if (!(error instanceof AiImageClientError)) return false;
  return ["RATE_LIMIT", "TEMPORARY_PROVIDER_UNAVAILABLE", "MODEL_UNAVAILABLE", "UPSTREAM", "NETWORK"].includes(
    error.errorCode ?? ""
  );
}

function resolveFirstPollinationsKey(keys?: string[]): string {
  const fromProps = (keys ?? []).find((key) => key?.trim());
  if (fromProps) return fromProps.trim();
  return getStoredPollinationsKeys()[0]?.trim() ?? "";
}

function decodeBase64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

function normalizeVariationSeed(seed?: number): number | undefined {
  if (typeof seed !== "number" || !Number.isFinite(seed)) return undefined;
  const normalized = Math.abs(Math.trunc(seed)) % 2_000_000_000;
  return normalized === 0 ? 1 : normalized;
}

export interface BuildAiImagePromptOptions {
  mode?: "initial" | "regenerate";
  deckTitle?: string;
}

export function buildAiImagePrompt(
  front: string,
  back?: string,
  imagePrompt?: string,
  options?: BuildAiImagePromptOptions
): string {
  return buildFlashcardImagePrompt(front, back, imagePrompt, {
    mode: options?.mode ?? "initial",
    deckTitle: options?.deckTitle,
  });
}

async function uploadAiImageFromBase64(userId: string, imageBase64: string, contentType: string): Promise<string> {
  const bytes = decodeBase64ToBytes(imageBase64);
  const blob = new Blob([bytes], { type: contentType || "image/jpeg" });
  if (!blob.type || !blob.type.startsWith("image/")) {
    throw new AiImageClientError("Invalid image payload", { errorCode: "INVALID_RESPONSE" });
  }

  const extension = contentType.includes("png") ? "png" : contentType.includes("webp") ? "webp" : "jpg";
  const fileName = `${userId}/${Date.now()}_ai.${extension}`;
  const { error } = await supabase.storage
    .from("card_images")
    .upload(fileName, blob, { contentType: contentType || "image/jpeg", upsert: false });
  if (error) {
    throw error;
  }

  const { data } = supabase.storage.from("card_images").getPublicUrl(fileName);
  return data.publicUrl;
}

export type AiImageResult =
  | { imageUrl: string; kind: "image"; cacheHit?: boolean; provider?: string; providerAssetId?: string; model?: string; source?: string }
  | { imageUrl: null; kind: "no_image" }
  | { imageUrl: null; kind: "no_new_image" };

export async function requestAiImageUrl({
  prompt,
  term,
  translationHint,
  sourceLanguage,
  targetLanguage,
  mode = "initial",
  excludeImageUrls,
  excludeProviderAssetIds,
  variationSeed,
  userId,
  pollinationsKeys,
}: RequestAiImageOptions): Promise<AiImageResult> {
  const pollinationsKey = resolveFirstPollinationsKey(pollinationsKeys);
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (pollinationsKey) {
    headers["X-Pollinations-Key"] = pollinationsKey;
  }
  const baseExcludedUrlSet = new Set((excludeImageUrls ?? []).map((value) => value.trim()).filter(Boolean));
  const baseExcludedProviderAssetIdSet = new Set(
    (excludeProviderAssetIds ?? []).map((value) => value.trim()).filter(Boolean)
  );
  const regenerateAttempts = mode === "regenerate" ? 4 : 1;
  let sameImageAllAttempts = false;
  const baseVariationSeed = normalizeVariationSeed(variationSeed);
  const rejectedProviderAssetIds: string[] = [];
  const rejectedImageUrls: string[] = [];

  for (let attemptIndex = 0; attemptIndex < regenerateAttempts; attemptIndex++) {
    const attemptExcludeImageUrls = [
      ...(excludeImageUrls ?? []),
      ...rejectedImageUrls,
    ].filter(Boolean);
    const attemptExcludeProviderAssetIds = [
      ...(excludeProviderAssetIds ?? []),
      ...rejectedProviderAssetIds,
    ].filter(Boolean);

    const response = await fetch("/api/generate-image", {
      method: "POST",
      headers,
      body: JSON.stringify({
        prompt,
        term,
        translationHint,
        sourceLanguage,
        targetLanguage,
        mode,
        excludeImageUrls: attemptExcludeImageUrls.length ? attemptExcludeImageUrls : undefined,
        excludeProviderAssetIds: attemptExcludeProviderAssetIds.length ? attemptExcludeProviderAssetIds : undefined,
        variationSeed:
          typeof baseVariationSeed === "number"
            ? baseVariationSeed + attemptIndex
            : undefined,
      }),
    });

    const payload = await response.json().catch(() => ({}));
    const normalized = normalizeImageApiPayload(payload, response.status, response.ok);

    if (normalized.kind === "no_image" || normalized.noImageControlled) {
      return { imageUrl: null, kind: "no_image" };
    }

    if (normalized.kind === "error" || !normalized.ok) {
      throw new AiImageClientError(normalized.errorMessage || "Image generation failed", {
        errorCode: normalized.errorCode,
        status: normalized.status,
        attempts: normalized.attempts as AiImageApiAttempt[] | undefined,
      });
    }

    if (normalized.imageUrl) {
      const currentExcludedUrls = new Set([...baseExcludedUrlSet, ...rejectedImageUrls]);
      const currentExcludedIds = new Set([...baseExcludedProviderAssetIdSet, ...rejectedProviderAssetIds]);
      const isSameAsCurrent =
        mode === "regenerate" &&
        (currentExcludedUrls.has(normalized.imageUrl) ||
          (normalized.providerAssetId ? currentExcludedIds.has(normalized.providerAssetId) : false));
      if (isSameAsCurrent) {
        console.info("[image-api] image_candidate_same_as_current_rejected", {
          provider: normalized.provider,
          attempt: attemptIndex + 1,
        });
        if (normalized.providerAssetId) rejectedProviderAssetIds.push(normalized.providerAssetId);
        if (normalized.imageUrl) rejectedImageUrls.push(normalized.imageUrl);
        sameImageAllAttempts = true;
        console.info("[image-api] image_provider_try_next_candidate", {
          attempt: attemptIndex + 1,
          rejectedCount: rejectedProviderAssetIds.length + rejectedImageUrls.length,
        });
        continue;
      }
      return {
        imageUrl: normalized.imageUrl,
        kind: "image" as const,
        cacheHit: normalized.cacheHit,
        provider: normalized.provider,
        providerAssetId: normalized.providerAssetId,
        model: normalized.model,
        source: normalized.source,
      };
    }

    if (normalized.imageBase64) {
      const publicUrl = await uploadAiImageFromBase64(
        userId,
        normalized.imageBase64,
        normalized.contentType || "image/jpeg"
      );
      const currentExcludedUrls = new Set([...baseExcludedUrlSet, ...rejectedImageUrls]);
      const currentExcludedIds = new Set([...baseExcludedProviderAssetIdSet, ...rejectedProviderAssetIds]);
      const isSameAsCurrent =
        mode === "regenerate" &&
        (currentExcludedUrls.has(publicUrl) ||
          (normalized.providerAssetId ? currentExcludedIds.has(normalized.providerAssetId) : false));
      if (isSameAsCurrent) {
        console.info("[image-api] image_candidate_same_as_current_rejected", {
          provider: normalized.provider,
          attempt: attemptIndex + 1,
        });
        if (normalized.providerAssetId) rejectedProviderAssetIds.push(normalized.providerAssetId);
        if (publicUrl) rejectedImageUrls.push(publicUrl);
        sameImageAllAttempts = true;
        console.info("[image-api] image_provider_try_next_candidate", {
          attempt: attemptIndex + 1,
          rejectedCount: rejectedProviderAssetIds.length + rejectedImageUrls.length,
        });
        continue;
      }
      return {
        imageUrl: publicUrl,
        kind: "image" as const,
        cacheHit: normalized.cacheHit,
        provider: normalized.provider,
        providerAssetId: normalized.providerAssetId,
        model: normalized.model,
        source: normalized.source,
      };
    }
  }

  if (sameImageAllAttempts && mode === "regenerate") {
    console.info("[image-api] image_no_new_image_found");
    return { imageUrl: null, kind: "no_new_image" };
  }

  throw new AiImageClientError("Invalid image payload", { errorCode: "INVALID_RESPONSE" });
}

export function getAiImageToastKey(error: unknown): string {
  const errorCode = error instanceof AiImageClientError ? error.errorCode : undefined;
  switch (errorCode) {
    case "CONFIG":
      return "deckModal.aiImageNoKey";
    case "UNAUTHORIZED":
    case "FORBIDDEN":
      return "deckModal.aiImageAccessError";
    case "RATE_LIMIT":
    case "TEMPORARY_PROVIDER_UNAVAILABLE":
      return "deckModal.aiImageServerBusy";
    case "MODEL_UNAVAILABLE":
    case "UPSTREAM":
    case "NETWORK":
      return "deckModal.aiImageServerUnavailable";
    default:
      return "deckModal.aiImageError";
  }
}
